import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Admin from "@/pages/Admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function AdminGate() {
  const { user, loading, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="flex h-[60vh] items-center justify-center text-slate-400">Loading…</div>;

  // Authenticated admin -> dashboard
  if (user && user.is_admin) return <Admin />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (!u.is_admin) {
        toast.error("This account is not an administrator.");
        return;
      }
      toast.success("Welcome, admin.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-900 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-800 p-8 shadow-xl" data-testid="admin-login-card">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white"><Shield className="h-6 w-6" /></div>
          <h1 className="font-heading text-2xl font-bold text-white">Admin Portal</h1>
          <p className="text-sm text-slate-400">SellStuff staff access only</p>
        </div>

        {user && !user.is_admin && (
          <div className="mb-4 flex items-center justify-between rounded-xl bg-amber-500/10 p-3 text-sm text-amber-300" data-testid="admin-not-authorized">
            <span>Signed in as a non-admin account.</span>
            <button onClick={logout} className="flex items-center gap-1 font-medium underline"><LogOut className="h-3 w-3" /> Sign out</button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
            <Input id="admin-email" data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-lg border-slate-600 bg-slate-900 text-white" placeholder="Email address" />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
            <Input id="admin-password" data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 rounded-lg border-slate-600 bg-slate-900 text-white" placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="admin-login-submit" className="w-full rounded-full bg-primary font-semibold hover:bg-primary/90">
            {submitting ? "Signing in…" : "Sign in to Admin"}
          </Button>
        </form>
      </div>
    </div>
  );
}
